import { useEffect, useState, Suspense } from 'react';
import { lazyWithReload } from '@/lib/lazyWithReload';
import { marcarHito } from '@/lib/analytics/hitosDelArranque';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { PageLoader } from '@/routes/PageLoader';
import { useSpaInternalNavCapture } from '@/hooks/useSpaInternalNavCapture';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { Frame, Box, InlineStack, Text, Button } from '@/lib/polaris';
import { AdminToastProvider } from '@/contexts/AdminToastContext';
import { useNotificacionesEnVivo } from '@/hooks/useNotificacionesEnVivo';
import { CallCenterProvider } from '@/contexts/CallCenterContext';
import { UnsavedChangesProvider, useUnsavedChanges } from '@/contexts/UnsavedChangesContext';
import { AdminGlobalSearch } from '@/components/polaris/AdminGlobalSearch';
/*
 * 🚨 EL MARCADOR SE IMPORTABA ESTATICO Y EL LAYOUT LO VE TODO EL MUNDO. Medido sobre el
 *    sourcemap: FloatingDialerWidget 43 KB + CallCenterContext 36 KB + telnyxWebRTCStore 28 KB =
 *    107 KB en el arranque de CUALQUIERA, use o no el call center.
 *
 * ⭐ Perezoso y dentro de su Suspense: sigue estando siempre disponible, pero su codigo llega
 *    despues de que la pantalla pinto. Un widget flotante no es lo primero que alguien mira.
 */
const FloatingDialerWidget = lazyWithReload(() => import('@/components/callCenter/FloatingDialerWidget').then((m) => ({ default: m.FloatingDialerWidget })));
import { ErrorBoundarySection } from '@/components/ErrorBoundarySection';
import { useSidebarCollapsed } from '@/hooks/useSidebarCollapsed';
import { useFeatureFlags } from '@smarty/shared';
import { AICopilotProvider } from '@/contexts/AICopilotContext';
import { AICopilotPanel } from '@/components/ai/AICopilotPanel';
import { CopilotDrawer } from '@/components/ai/CopilotDrawer';
import { FloatingSpeedDial } from '@/components/layout/FloatingSpeedDial';
import { SugerirMejoraHost } from '@/components/propuestas/SugerirMejoraHost';
import { ScrollToTop } from '@/components/layout/ScrollToTop';
import { WaInboxNavProvider } from '@/contexts/WaInboxNavContext';
import { WaChatDockProvider } from '@/contexts/WaChatDockContext';
/*
 * 🚨 EL DOCK DE CHAT TAMBIEN ENTRABA POR EL LAYOUT. Medido: WaChatComposer 90 KB +
 *    WaMessageRow 59 KB + WaMessageThread 53 KB = 202 KB en el arranque de todos, aunque la
 *    sesion no abra Chats ni una vez. Mismo mecanismo que el marcador telefonico.
 *
 * ⭐ Perezoso: el dock aparece cuando alguien lo abre, y su codigo llega en ese momento.
 */
const WaChatDock = lazyWithReload(() => import('@/components/whatsapp/WaChatDock').then((m) => ({ default: m.WaChatDock })));
// El aviso «Importante» de las novedades: se baja aparte, porque la mayoría de las veces no hay ninguno.
const AvisoDeNovedadImportante = lazyWithReload(() => import('@/components/novedades/AvisoDeNovedadImportante').then((m) => ({ default: m.AvisoDeNovedadImportante })));
import { WaInboxNavigation } from '@/components/whatsapp/WaInboxNavigation';
import { CallCenterNavigation } from '@/components/callCenter/CallCenterNavigation';
import { AdsNavigation } from '@/components/ads/AdsNavigation';
import { AgentsNavigation } from '@/components/agents/AgentsNavigation';

const BODY_ADMIN_CLASS = 'polaris-admin';
const HTML_ADMIN_CLASS = 'polaris-admin-doc';

/** Botón flotante del copiloto — omnipresente en todo el admin, solo para planes con
 *  la feature 'copilot' (enterprise). Abre el copiloto NUEVO (43 acciones, edge fn
 *  crm-copilot) en un drawer, sin salir de la pantalla. NO es el asistente viejo. */
function CopilotFab() {
  const { isFeatureEnabled } = useFeatureFlags();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  if (!isFeatureEnabled('copilot')) return null;
  // Se oculta el FAB en pantallas full-bleed (y en la propia página del copiloto).
  const HIDDEN = ['/chats/inbox', '/apps/crm/inbox', '/llamadas/dialer', '/apps/crm/copilot'];
  const hideFab = HIDDEN.some((r) => pathname.includes(r));
  return (
    <>
      {!hideFab && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Abrir copiloto"
          title="Copiloto"
          style={{
            position: 'fixed', bottom: 24, right: 24, zIndex: 99990,
            width: 48, height: 48, borderRadius: '50%', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
            background: 'linear-gradient(135deg, #2563EB, #4F46E5)',
            transition: 'transform 0.2s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
        >
          <span style={{ fontSize: 20, lineHeight: 1 }}>✨</span>
        </button>
      )}
      <CopilotDrawer open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/** Barra de cambios no guardados (estilo Shopify: arriba en la barra) */
function UnsavedChangesBar() {
  const { unsaved } = useUnsavedChanges();
  /**
   * 🪤 El botón no tenía estado de guardando: durante un guardado lento seguía clickeable, y el
   * segundo clic disparaba OTRO guardado en paralelo — en "crear" eso es el registro duplicado.
   * Las páginas encendían su propio `saving` pero nadie lo leía; el spinner tiene que vivir acá,
   * que es donde está el botón que todas comparten.
   */
  const [guardando, setGuardando] = useState(false);
  if (!unsaved) return null;
  return (
    <Box padding="300" paddingBlockEnd="200" background="bg-fill-inverse">
      <InlineStack align="space-between" blockAlign="center" gap="300">
        <Text as="span" variant="bodySm" tone="text-inverse">
          Cambios no guardados
        </Text>
        <InlineStack gap="200" blockAlign="center">
          {/* Descartar también se bloquea durante el guardado: descartar MIENTRAS se guarda es una
              carrera cuyo resultado depende de qué promesa gane. */}
          <Button size="slim" variant="secondary" tone="inverse" disabled={guardando} onClick={unsaved.onDiscard}>
            Descartar
          </Button>
          {/* onSave puede rechazar (ver contrato en UnsavedChangesContext): la página ya
              mostró su toast de error, así que acá solo evitamos el unhandled rejection. */}
          <Button
            size="slim"
            variant="primary"
            tone="inverse"
            loading={guardando}
            onClick={() => {
              setGuardando(true);
              void Promise.resolve(unsaved.onSave())
                .catch(() => {})
                .finally(() => setGuardando(false));
            }}
          >
            Guardar
          </Button>
        </InlineStack>
      </InlineStack>
    </Box>
  );
}

/**
 * Layout admin (estilo Shopify).
 * Frame + Sidebar (Polaris Navigation) + TopBar. AdminGlobalSearch fuera del Frame.
 * polaris-admin en body evita estilos Tailwind. Modal/espaciado override en polaris.css.
 */
/** Sin pintura propia: existe para que el hook viva dentro de los proveedores que necesita. */
function AvisosEnVivo() {
  useNotificacionesEnVivo();
  return null;
}

/** Anota el momento en que el marco quedo armado y le toca el turno a la pantalla. */
function MarcaDelMarco() {
  marcarHito('marco');
  return null;
}
export function AppLayout() {

  /*
   * El cascaron empieza a montarse: onceavo proveedor, `Frame`, Sidebar y Topbar antes de que
   * cualquier pantalla exista.
   *
   * 🚨 SE MIDE PORQUE ES EL UNICO TRAMO QUE QUEDA Y ES EL MAS GRANDE. A los 532 ms ya no falta
   *    nada —codigo, permisos y datos estan— y la pantalla monta 317 ms despues. Entre la sonda
   *    (que vive arriba de este layout) y la pantalla hay 478 ms que hasta ahora eran una sola
   *    caja negra.
   *
   * 🪤 VA EN EL CUERPO, NO EN UN EFECTO: un efecto corre despues del commit, y lo que se quiere
   *    saber es cuando EMPEZO a montarse el layout, no cuando termino.
   */
  marcarHito('cascaron');
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { collapsed, isWorkspace, toggle: toggleSidebar } = useSidebarCollapsed();

  /*
   * 🚨 TOCAR AFUERA PARA CERRAR EL MENÚ TAMBIÉN APRETABA EL BOTÓN QUE HABÍA DEBAJO.
   *
   * El founder, desde su iPhone: «si le doy click afuera y de casualidad ese click chocó en un
   * botón, también se procesa ese botón». Cerrar un menú nunca debería ejecutar nada.
   *
   * LA CAUSA ESTÁ EN POLARIS, no en nuestro código. Su `Frame` cierra el menú con el evento
   * `touchstart` (`onTouchStart: this.handleNavigationDismiss`): el dedo toca el fondo, el menú se
   * cierra y el telón se desmonta EN ESE INSTANTE. Después el navegador emite el `click` que
   * siempre sigue a un toque — y como el telón ya no está, ese click aterriza en lo que quedó
   * debajo. En una computadora no pasa: ahí solo hay `click`, no la pareja `touchstart` + `click`.
   *
   * 🪤 `preventDefault()` sobre el `touchstart` DEL TELÓN es lo que corta esa pareja: el navegador
   * ya no emite el click fantasma. No impide que el menú se cierre —el manejador de Polaris corre
   * igual, `preventDefault` no cancela manejadores— ni afecta a los toques dentro del menú.
   *
   * Va en fase de captura y con `passive: false`, porque un listener pasivo no puede prevenir nada.
   */
  useEffect(() => {
    if (!mobileNavOpen) return;

    const alTocarElTelon = (evento: TouchEvent) => {
      const destino = evento.target as HTMLElement | null;
      if (destino?.classList.contains('Polaris-Backdrop')) evento.preventDefault();
    };

    document.addEventListener('touchstart', alTocarElTelon, { capture: true, passive: false });
    return () => document.removeEventListener('touchstart', alTocarElTelon, { capture: true });
  }, [mobileNavOpen]);
  const navigate = useNavigate();
  const location = useLocation();
  useSpaInternalNavCapture(navigate);

  // Sidebar contextual: en cualquier ruta /chats/* (inbox, settings, agents, tags,
  // etc.) el nav global se reemplaza por WaInboxNavigation, estilo Chatwoot — el
  // sidebar del módulo solo desaparece al salir de Chats con "Volver".
  // (Se mantiene /whatsapp por las URLs legacy, que igual redirigen a /chats.)
  const isInboxRoute = /\/(chats|whatsapp)(\/|$)/.test(location.pathname);

  // Sidebar contextual de Llamadas (ex Call Center / Voice Commerce). Mismo patrón
  // que Chats: módulo-as-app con su propio mundo. Activo en todo /llamadas/*
  // (marcador, historial, voice-ai, coaching, campañas, supervisión, etc.).
  // Se mantienen los prefijos viejos por las URLs legacy en tránsito al redirect.
  const isCallCenterRoute =
    /\/llamadas(\/|$)/.test(location.pathname) ||
    /\/apps\/call-center(\/|$)/.test(location.pathname) ||
    /\/apps\/crm\/(dialer|calls|voice-ai|call-coaching)(\/|$)/.test(location.pathname);

  // Sidebar contextual del módulo Smarty Ads. Mismo patrón módulo-as-app
  // que Chats / Llamadas: en /ads/* el sidebar global se reemplaza
  // por AdsNavigation con secciones propias (Campañas, Chat senior IA,
  // Crecimiento, Cuenta).
  const isAdsRoute = /\/ads(\/|$)/.test(location.pathname);

  // Sidebar contextual de Agentes IA. Mismo patrón módulo-as-app que Chats/Llamadas/Ads.
  // Incluye Flujos (/apps/chatbot), que es parte del módulo aunque viva bajo /apps.
  // 🪤 Va DESPUÉS de isInboxRoute en el ternario: /chats/agents es de Chats, no de este módulo.
  const isAgentsRoute =
    /\/agents(\/|$)/.test(location.pathname) && !isInboxRoute
    || /\/apps\/chatbot(\/|$)/.test(location.pathname);

  useEffect(() => {
    document.documentElement.classList.add(HTML_ADMIN_CLASS);
    document.body.classList.add(BODY_ADMIN_CLASS);
    return () => {
      document.body.classList.remove(BODY_ADMIN_CLASS);
      document.documentElement.classList.remove(HTML_ADMIN_CLASS);
    };
  }, []);

  // Evitar recarga al elegir 2º+ valor en presets: submit, submit programático, clics y mousedown
  // en <a href="">/# y en button[type=submit] sin data-allow-submit. mousedown evita que el navegador
  // dispare la acción antes del click.
  useEffect(() => {
    const allowSubmit = (form: HTMLFormElement | null) => form?.getAttribute('data-allow-submit') === 'true';

    const submitHandler = (e: Event) => {
      const form =
        e.target instanceof HTMLFormElement
          ? e.target
          : (e.target as Node) && 'closest' in (e.target as Element)
            ? (e.target as Element).closest('form')
            : null;
      if (allowSubmit(form)) return;
      e.preventDefault();
      e.stopPropagation();
    };

    const nativeSubmit = HTMLFormElement.prototype.submit;
    HTMLFormElement.prototype.submit = function (this: HTMLFormElement) {
      if (!allowSubmit(this)) return;
      return nativeSubmit.call(this);
    };

    const preventNavigation = (e: MouseEvent) => {
      const target = e.target instanceof Element ? e.target : null;
      if (!target) return;
      const anchor = target.closest('a');
      if (anchor instanceof HTMLAnchorElement) {
        const href = (anchor.getAttribute('href') ?? '').trim();
        if (href === '' || href === '#') {
          e.preventDefault();
          e.stopPropagation();
        }
        return;
      }
      const submitBtn = target.closest('button[type="submit"]');
      if (submitBtn) {
        const form = submitBtn.closest('form');
        if (form && !allowSubmit(form)) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };

    document.addEventListener('submit', submitHandler, true);
    document.addEventListener('click', preventNavigation, true);
    document.addEventListener('mousedown', preventNavigation, true);
    return () => {
      document.removeEventListener('submit', submitHandler, true);
      document.removeEventListener('click', preventNavigation, true);
      document.removeEventListener('mousedown', preventNavigation, true);
      HTMLFormElement.prototype.submit = nativeSubmit;
    };
  }, []);

  const topBar = (
    <Box style={{ position: 'relative' }}>
      <Topbar
        onOpenSearch={() => setSearchOpen(true)}
        onNavigationToggle={() => setMobileNavOpen(true)}
        sidebarCollapsed={collapsed}
        onToggleSidebar={toggleSidebar}
      />
      {/*
        🪤 EL `id` NO ES DECORATIVO: por él la barra sabe callarse. Cuando el editor de venta del
        chat está abierto, el guardado vive en el pie de ESE panel —junto a lo que se está
        editando— y esta barra quedaría como un segundo botón de guardar para el mismo cambio,
        encima medio tapada por el panel. Dos avisos de "cambios sin guardar" no avisan el doble:
        obligan a decidir cuál apretar. La regla vive en el CSS del panel, que es quien la impone.
      */}
      <Box
        id="admin-barra-cambios"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 10000,
        }}
      >
        <UnsavedChangesBar />
      </Box>
    </Box>
  );

  return (
    <AICopilotProvider>
      <UnsavedChangesProvider>
        <WaInboxNavProvider>
         {/* Envuelve al Frame para que cualquier pantalla del admin pueda abrir un chat acoplado. */}
         <WaChatDockProvider>
          <Frame
            navigation={
              isInboxRoute
                ? <WaInboxNavigation />
                : isCallCenterRoute
                  ? <CallCenterNavigation />
                  : isAdsRoute
                    ? <AdsNavigation />
                    : isAgentsRoute
                      ? <AgentsNavigation />
                      : <Sidebar onDismiss={() => setMobileNavOpen(false)} />
            }
            topBar={topBar}
            showMobileNavigation={mobileNavOpen}
            onNavigationDismiss={() => setMobileNavOpen(false)}
          >
            <AdminToastProvider>
              {/*
                ⭐ LOS AVISOS EN VIVO, PARA TODA LA APP. Va acá dentro y no en una pantalla concreta
                   porque una recarga se aprueba mientras el revendedor está en cualquier lado — y
                   enterarse sólo si tenía abierta la página del saldo no es enterarse en vivo.

                🪤 Dentro de `AdminToastProvider`: el hook avisa con un toast, y afuera del proveedor
                   ese contexto no existe.
              */}
              <AvisosEnVivo />
              <Suspense fallback={null}><AvisoDeNovedadImportante /></Suspense>
              <CallCenterProvider>
                {/* Al cambiar de ruta, vuelve el scroll arriba (el admin scrollea en #root, no el window). */}
                <ScrollToTop />
                <Box as="div" paddingBlockEnd={isWorkspace ? '0' : '800'} minHeight="100%">
                  {/* Suspense de nivel de layout: permite lazy-cargar cualquier
                      página del admin sin envolver ruta por ruta. El core (Dashboard,
                      Productos…) sigue eager; canales/apps suspenden brevemente. */}
                  <Suspense fallback={<PageLoader variant="content" />}>
                    {/*
                      El marco ya esta armado y toca el turno de la pantalla. Entre `barra` y esto
                      vive el costo de la barra lateral; entre esto y `montado`, el arbol de rutas.

                      🪤 Va en un componente y no como `{marcarHito(...)}` suelto: eso devuelve
                         `void`, que JSX no acepta como hijo. Un componente que marca y no pinta
                         nada es la forma limpia de anotar un punto del arbol.
                    */}
                    <MarcaDelMarco />
                    <Outlet />
                  </Suspense>
                </Box>
                {/* H1 I4: aislar errores del marcador para no tumbar el resto de la app. */}
                <ErrorBoundarySection sectionName="Marcador" fallback={null}>
                  <Suspense fallback={null}><FloatingDialerWidget hideFab /></Suspense>
                </ErrorBoundarySection>
                {/*
                  * El dock se MONTA acá adentro —necesita el toast y la sesión— pero se DIBUJA por
                  * portal al body: dentro del Frame, position:fixed se ancla al contenedor y no a
                  * la ventana. Mismo arreglo que usa el marcador.
                  */}
                <ErrorBoundarySection sectionName="Chats acoplados" fallback={null}>
                  <Suspense fallback={null}><WaChatDock /></Suspense>
                </ErrorBoundarySection>
                {/*
                  * Va DENTRO del Frame aunque lo dispare el speed dial, que está fuera:
                  * el modal usa Polaris y el toast, y los dos necesitan el Frame. El
                  * speed dial solo emite un evento, así que no le importa dónde vive
                  * quien lo escucha. No monta nada hasta que alguien lo abre.
                  */}
                <ErrorBoundarySection sectionName="Sugerir mejora" fallback={null}>
                  <SugerirMejoraHost />
                </ErrorBoundarySection>
              </CallCenterProvider>
            </AdminToastProvider>
          </Frame>
         </WaChatDockProvider>
        </WaInboxNavProvider>
      </UnsavedChangesProvider>
      {/* Fuera del Frame para que position:fixed funcione sobre toda la ventana */}
      <FloatingSpeedDial />
      <AICopilotPanel />
      <CopilotFab />
      <AdminGlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </AICopilotProvider>
  );
}
